import { c } from 'ttag';

import { IDEAL_WERO_BRAND_NAME } from '@proton/chargebee/lib/constants';
import type { IconComponent } from '@proton/icons/component';
import type { ViewPaymentMethod } from '@proton/payments-ui/client-extensions/useMethods';
import { PAYMENT_METHOD_TYPES } from '@proton/payments/core/constants';
import type { AvailablePaymentMethod, PaymentMethodType } from '@proton/payments/core/interface';
import americanExpressSafekeySvg from '@proton/styles/assets/img/bank-icons/amex-safekey-colored.svg';
import discoverProtectBuySvg from '@proton/styles/assets/img/bank-icons/discover-protectbuy-colored.svg';
import googlePayMarkSvg from '@proton/styles/assets/img/bank-icons/google-pay-mark.svg';
import idealMarkSvg from '@proton/styles/assets/img/bank-icons/ideal.svg';
import jcbLogoSvg from '@proton/styles/assets/img/bank-icons/jcb-logo.png';
import mastercardSecurecodeSvg from '@proton/styles/assets/img/bank-icons/mastercard-securecode-colored.svg';
import unionpayLogoSvg from '@proton/styles/assets/img/bank-icons/unionpay-logo.svg';
import verifiedByVisaSvg from '@proton/styles/assets/img/bank-icons/visa-secure-colored.svg';
import clsx from '@proton/utils/clsx';

import type { DropdownSize } from '../../../components/dropdown/utils';
import { DropdownSizeUnit } from '../../../components/dropdown/utils';
import Radio from '../../../components/input/Radio';
import Option from '../../../components/option/Option';
import SelectTwo from '../../../components/selectTwo/SelectTwo';

interface Props {
    options: ViewPaymentMethod[];
    method?: PaymentMethodType;
    onChange: (paymentMethod: AvailablePaymentMethod) => void;
    lastUsedMethod?: ViewPaymentMethod;
    forceDropdown?: boolean;
    narrow?: boolean;
    showCardIcons?: boolean;
    disabled?: boolean;
}

const PaymentMethodIcon = ({
    icon: Icon,
    className,
    type,
}: {
    icon?: IconComponent;
    className?: string;
    type: PaymentMethodType;
}) => {
    if (type === PAYMENT_METHOD_TYPES.GOOGLE_PAY) {
        return (
            <span className="mr-2 w-custom" style={{ '--w-custom': '2.5rem' }}>
                <img
                    src={googlePayMarkSvg}
                    alt={c('Info').t`Google Pay mark`}
                    style={{ maxHeight: '1.25rem', marginTop: '-0.15625rem' }}
                />
            </span>
        );
    }

    if (type === PAYMENT_METHOD_TYPES.CHARGEBEE_IDEAL) {
        return (
            <span className="mr-2 w-custom" style={{ '--w-custom': '3.5rem' }}>
                <img
                    src={idealMarkSvg}
                    alt={c('Info').t`${IDEAL_WERO_BRAND_NAME} mark`}
                    style={{ marginTop: '-0.15625rem' }}
                />
            </span>
        );
    }

    if (!Icon) {
        return null;
    }

    return <Icon className={className} />;
};

const PaymentMethodSelector = ({
    method,
    lastUsedMethod,
    options,
    onChange,
    forceDropdown,
    narrow,
    showCardIcons = false,
    disabled,
}: Props) => {
    if (options.length <= 1) {
        // Not helpful to show this if there's only a single option
        return null;
    }

    const hasSavedPaymentMethod = options.find((it) => it.isSaved);
    const showRadioButtons =
        (options.length <= 2 && !forceDropdown) ||
        // if user already has a saved payment method, we don't need to show all the payment methods at once
        !hasSavedPaymentMethod;

    if (showRadioButtons) {
        return (
            <>
                {options.map((viewPaymentMethod) => {
                    const { text, value, icon, type } = viewPaymentMethod;

                    return (
                        <label
                            htmlFor={value}
                            key={value}
                            className={clsx([
                                'py-2 flex items-center gap-2',
                                lastUsedMethod?.value === value && 'border-bottom',
                            ])}
                        >
                            <span className="flex items-center flex-nowrap">
                                <Radio
                                    className="mr-2"
                                    id={value}
                                    name="value"
                                    disabled={disabled}
                                    checked={value === method}
                                    onChange={() => onChange(viewPaymentMethod)}
                                    data-testid={`payment-method-${value}`}
                                />
                                {<PaymentMethodIcon icon={icon} type={type} className="mr-2" />}
                                <span className="text-cut text-nowrap">{text}</span>
                            </span>

                            {
                                // Only show suffix icons for card payments
                                showCardIcons && value === 'chargebee-card' && (
                                    <div className="flex items-center justify-center gap-2 flex-nowrap ml-6">
                                        <img
                                            alt={c('Info').t`Visa Secure logo`}
                                            style={{ maxHeight: '26px' }}
                                            src={verifiedByVisaSvg}
                                        />
                                        <img
                                            alt={c('Info').t`Mastercard SecureCode logo`}
                                            style={{ maxHeight: '26px' }}
                                            src={mastercardSecurecodeSvg}
                                        />
                                        <img
                                            alt={c('Info').t`Discover ProtectBuy logo`}
                                            style={{ maxHeight: '26px' }}
                                            src={discoverProtectBuySvg}
                                        />
                                        <img
                                            alt={c('Info').t`American Express SafeKey logo`}
                                            style={{ maxHeight: '26px' }}
                                            src={americanExpressSafekeySvg}
                                        />
                                        <img
                                            alt={c('Info').t`JCB logo`}
                                            style={{ maxHeight: '26px' }}
                                            src={jcbLogoSvg}
                                        />
                                        <img
                                            alt={c('Info').t`UnionPay logo`}
                                            style={{ maxHeight: '26px' }}
                                            src={unionpayLogoSvg}
                                        />
                                    </div>
                                )
                            }
                        </label>
                    );
                })}
            </>
        );
    }

    const size: DropdownSize | undefined = narrow
        ? {
              width: DropdownSizeUnit.Dynamic,
          }
        : undefined;

    return (
        <SelectTwo
            id="select-method"
            value={method}
            disabled={disabled}
            onChange={({ value }) => {
                const viewPaymentMethod = options.find((option) => option.value === value);
                if (viewPaymentMethod) {
                    onChange(viewPaymentMethod);
                }
            }}
            className={clsx(narrow && 'w-auto')}
            size={size}
            data-testid="payment-method-selector"
        >
            {options.flatMap((option) => {
                const child = (
                    <Option
                        key={option.value}
                        value={option.value}
                        title={option.text}
                        data-testid={`payment-method-${option.value}`}
                    >
                        <span className="inline-flex max-w-full flex-nowrap justify-start">
                            <PaymentMethodIcon
                                icon={option.icon}
                                type={option.type}
                                className="mr-2 my-auto shrink-0"
                            />
                            <span className="text-ellipsis">{option.text}</span>
                        </span>
                    </Option>
                );
                if (lastUsedMethod?.value === option.value) {
                    return [
                        child,
                        <div className="py-2 block" key={`${option.value}-separator`}>
                            <hr className="my-0" />
                        </div>,
                    ];
                }
                return child;
            })}
        </SelectTwo>
    );
};

export default PaymentMethodSelector;
